/**
 * SIL-1377 - a render-error boundary scoped to the marketplace RESULTS REGION.
 *
 * ## Why a second boundary, when `main.tsx` already has one
 *
 * `@siliconx/shared/ui/ErrorBoundary` sits at the root of the tree, so it is
 * correct and it is also the whole problem: its fallback is a full-viewport
 * "Something went wrong" card, which means one malformed listing row takes down
 * the header, the search input, the verify banner, the filters and the nav along
 * with it. That is the white screen this issue exists to remove - the user has
 * no way back except the browser's own controls.
 *
 * React only ever unwinds to the NEAREST boundary, so scoping one just inside
 * the results grid is the entire mechanism: a throw from any `ListingCard`
 * reaches this one first, the surrounding page is never unmounted, and the buyer
 * keeps a working page with a "couldn't load listings" placard where the grid
 * was. The guards in `listingShape` make the known skew shapes not throw; this is
 * the backstop for the ones nobody has met yet, which is the point - a guard is
 * a fix for a field you thought of, a boundary is a fix for the class.
 *
 * ## No Retry button
 *
 * A render error on listing data is DETERMINISTIC in that data: re-mounting the
 * same rows throws again, so a Retry would be a control that cannot work (the
 * same reason the listing detail withholds the node-Passport button from an
 * unverified buyer). Recovering means new data, which is what navigating or
 * changing a filter already does - and both of those still work here, because
 * the nav and the filter card are outside this boundary.
 *
 * ## What it does NOT catch
 *
 * Errors thrown from event handlers and async callbacks, same as every React
 * boundary; and a failed FETCH is not this component's job either - that is
 * `QueryBoundary`, which owns the loading/error/empty states of the request
 * itself. This catches errors thrown while RENDERING a successful response.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorState } from '@siliconx/shared/ui/state';
import { reportClientError } from '@siliconx/shared/telemetry/report-client-error';
import { API_BASE } from '../../lib/api';
import { MARKETPLACE_RESULTS_CRASH_TITLE } from '@siliconx/shared/ui/buyer-listing/copy/marketplace';

interface State {
  error: Error | null;
}

export class MarketplaceResultsBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  /**
   * Reported through the SAME `reportClientError` / POST /api/client-error path
   * as the root boundary (SIL-666), with `source: 'error-boundary'`. A
   * degraded-but-usable page is quieter than a white screen by design, so
   * without this the failure would stop reaching anyone - the incident that
   * produced this issue was noticed because the page was blank.
   */
  override componentDidCatch(error: Error, info: ErrorInfo): void {
    reportClientError(API_BASE, {
      message: error.message || String(error),
      name: error.name,
      stack: error.stack,
      componentStack: info.componentStack ?? undefined,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      portal: 'customer',
      source: 'error-boundary',
    });
  }

  override render(): ReactNode {
    if (this.state.error !== null) {
      return (
        <div data-testid="marketplace-results-crash">
          {/* The message names the REGION, not the app: the rest of the page is
              still there and telling the buyer otherwise would be false. The
              underlying error goes behind ErrorState's own Details disclosure,
              and to the log line above - not into the headline. */}
          <ErrorState title={MARKETPLACE_RESULTS_CRASH_TITLE} error={this.state.error} />
        </div>
      );
    }
    return this.props.children;
  }
}
